import React, { useCallback, useEffect, useState } from "react";
import { Redirect, useHistory, useLocation } from "react-router-dom";
import { IoCreate, IoRadioButtonOn } from "react-icons/io5";
import { apiMessage } from "../../api/apiMessage";
import { usePropertyRole } from "../../hooks/usePropertyOwner";
import { UserService } from "../../services/UserService";
import { User } from "../../storage/store";
import Main from "../../components/layout/Main";
import { BackButton } from "../../components/layout/app/AppHeader";
import AppModal from "../../components/layout/app/AppModal";
import AlertDialog from "../../components/layout/app/AlertDialog";
import { PLACES_CHANGED, placesChanged } from "../../components/places/places";
import BulkPower from "./BulkPower";
import { useToggleTop } from "../../components/common/ToggleTop";
import DeviceCard, { CardSkeletons } from "../../components/device/DeviceCard";
import { withLastState } from "../../hooks/usePropertyDevices";

// Room page as in the app (app/screens/RoomDetails.js): a tinted header with the
// room's name and how many devices are running, then its accessories.
function Room() {
  const location = useLocation();
  const navigate = useHistory();
  const toast = useToggleTop();
  const role = usePropertyRole();
  const params = location.state?.params;
  const [room, setRoom] = useState({ name: params?.roomName ?? "", imageUri: null, floor: params?.floorName ?? "" });
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [devices, setDevices] = useState(null);
  const [error, setError] = useState(false);
  const [editing, setEditing] = useState(false);
  const [newName, setNewName] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!params?.roomId) return;
    const propertyId = User.getPropertyId();
    try {
      const config = await UserService.getConfig(propertyId);
      let floorId = null;
      (config.floors ?? []).forEach((floor) =>
        (floor.rooms ?? []).forEach((item) => {
          if (item._id === params.roomId) {
            floorId = floor._id;
            setRoom({ name: item.name, imageUri: item.imageUri ?? null, floor: floor.name });
          }
        })
      );
      let lastStates = null;
      try {
        lastStates = await UserService.getLastStateByFloorRoomId(propertyId, floorId, params.roomId);
      } catch (err) {
        // Without last states the devices show as off.
      }
      const all = withLastState(config.devices ?? [], lastStates);
      setDevices(all.filter((device) => device.floorId === floorId && device.roomId === params.roomId));
      setError(false);
    } catch (err) {
      setDevices([]);
      setError(true);
    }
  }, [params?.roomId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    window.addEventListener(PLACES_CHANGED, load);
    return () => window.removeEventListener(PLACES_CHANGED, load);
  }, [load]);

  if (!params?.roomId) return <Redirect to="/floors" />;

  const running = devices ? devices.filter((device) => device.props?.state === true).length : 0;

  // Only an empty room can go, as in the app: the API removes a room by its
  // floor and name, devices or not.
  const deleteRoom = async () => {
    setConfirmDelete(false);
    if (devices?.length) return;
    try {
      await UserService.deleteRoom(User.getPropertyId(), { floor: room.floor, name: room.name });
      toast(`Room "${room.name}" deleted`, "green");
      placesChanged();
      navigate.push("/floors");
    } catch (err) {
      toast(apiMessage(err, "Couldn't delete the room. Please try again."), "red");
    }
  };

  // Renames the room in the property config, as the old room page did.
  const saveName = async (event) => {
    event.preventDefault();
    const name = newName.trim();
    if (!name || name === room.name) {
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      const propertyId = User.getPropertyId();
      const config = await UserService.getConfig(propertyId);
      config.floors = config.floors.map((floor) => ({
        ...floor,
        rooms: floor.rooms.map((item) => (item._id === params.roomId ? { ...item, name } : item)),
      }));
      await UserService.updateConfig(config, propertyId);
      toast("Room name updated", "green");
      setEditing(false);
      load();
    } catch (err) {
      toast(apiMessage(err, "Couldn't update the room name"), "red");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Main header="none" bleed>
      <section
        className="hx-room-hero"
        style={{ backgroundImage: room.imageUri ? `url("${room.imageUri}")` : undefined }}
        aria-label={room.name}
      >
        <h1 className="hx-room-hero-name">{room.name}</h1>
        <div className="hx-room-hero-bar">
          <div className="hx-header-inner">
            <BackButton onBack={() => navigate.push("/floors")} />
            <button
              type="button"
              className="hx-icon-btn hx-edit"
              onClick={() => {
                // The API only lets owners change the property.
                if (role === "guest") {
                  toast("Only an owner can edit rooms.", "red");
                  return;
                }
                setNewName(room.name);
                setEditing(true);
              }}
              aria-label="Rename room"
            >
              <IoCreate />
            </button>
          </div>
        </div>
        <div className="hx-room-hero-foot">
          <div className="hx-page">
            <span className="hx-room-stat">
              <IoRadioButtonOn style={{ color: "#47e888" }} aria-hidden="true" />
              {devices === null ? "…" : `${running || "No"} devices running`}
            </span>
          </div>
        </div>
      </section>

      <div className="hx-page">
        <h2 className="hx-section-title">Accessories</h2>
        {error ? (
          <div className="hx-empty" role="alert">
            Couldn't load this room. Check your connection and reload the page.
          </div>
        ) : (
          <div className="hx-grid">
            {devices === null ? (
              <CardSkeletons count={6} />
            ) : devices.length ? (
              devices.map((device) => <DeviceCard key={device.applianceId ?? device.deviceId} device={device} />)
            ) : (
              <div className="hx-empty" style={{ gridColumn: "1 / -1" }}>
                No devices in this room yet.
                {role === "owner" ? (
                  <button type="button" className="hx-btn is-ghost is-danger-text" onClick={() => setConfirmDelete(true)}>
                    Delete room
                  </button>
                ) : null}
              </div>
            )}
          </div>
        )}
      </div>

      {devices?.length ? <BulkPower devices={devices} onDone={load} scope="room" /> : null}

      <AlertDialog
        open={confirmDelete}
        type="confirmation"
        title="Delete Room"
        message={`Are you sure you want to delete the room "${room.name}"?`}
        confirmLabel="Delete"
        onClose={() => setConfirmDelete(false)}
        onConfirm={deleteRoom}
      />

      <AppModal
        open={editing}
        onClose={() => setEditing(false)}
        title="Edit Room"
        actions={
          <>
            <button type="button" className="hx-btn is-ghost" onClick={() => setEditing(false)}>
              Cancel
            </button>
            <button type="submit" form="hx-room-form" className="hx-btn" disabled={saving}>
              {saving ? "Saving…" : "Update"}
            </button>
          </>
        }
      >
        <form id="hx-room-form" onSubmit={saveName}>
          <label className="hx-label" htmlFor="hx-room-name">
            Room name
          </label>
          <input
            id="hx-room-name"
            className="hx-input"
            value={newName}
            onChange={(event) => setNewName(event.target.value)}
            autoComplete="off"
          />
        </form>
      </AppModal>
    </Main>
  );
}

export default Room;
